import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Dateable } from 'pkg/api/models/dateable';
import type { Record } from 'pkg/api/models/record';
import type { SoftDeletable } from 'pkg/api/models/soft_deletable';

export enum AgreementType {
	Waiver = 'waiver',
}

export enum ConsentInteractionType {
	Required = 'required',
	Optional = 'optional',
}

export enum AgreementUsageType {
	Form = 'form',
}

export interface AgreementUsageRow {
	formId: number;
	groupId: number;
	formTitle: string;
	usageType: AgreementUsageType;
	signed: number;
}

export interface Agreement
	extends Record, Dateable, SoftDeletable, AllowedActionable {
	groupId: number;
	userId: number;
	type: AgreementType;
	internalName: string;
	title: string;
	content: string;
}

/**
 * Link between a form and an agreement the submitter must consent to. Returned
 * on the form show/public-show responses as `form.formAgreements`. The nested
 * `agreement` carries the title/content rendered to the submitter.
 */
export interface FormAgreementLink {
	agreementId: number;
	consentInteractionType: ConsentInteractionType;
	agreement?: Agreement;
}

export enum FormSubmissionAgreementStatus {
	Invalid = 'invalid',
	Accepted = 'accepted',
	Declined = 'declined',
}

/**
 * Immutable snapshot of a submitter's accept/decline outcome against an
 * agreement (waiver), captured at submission time. Returned scrubbed on the
 * submission show response as `submissionAgreements`.
 */
export interface FormSubmissionAgreement {
	status: FormSubmissionAgreementStatus;
	title?: string;
	content?: string;
	signedByName?: string;
	contentHash?: string;
}

export interface CreatePayload {
	name: string;
	title: string;
	content: string;
	type: AgreementType;
}

export type InitialValues = Partial<Omit<CreatePayload, 'type'>>;

export interface ShowResponse {
	agreement: Agreement;
	usage: AgreementUsageRow[];
}

export interface ListItem {
	id: number;
	internalName: string;
	title: string;
	content: string;
	createdAt: number;
}
